import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import serverURL from '../config';
import CandidateSelect from './CandidateSelect';
import toast from 'react-hot-toast';
import { BsGrid3X3Gap, BsList } from 'react-icons/bs';
import { FaSearch } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const CandidatesList = () => {
    const { id } = useParams();
    const [candidates, setCandidates] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCandidates, setFilteredCandidates] = useState([]);
    const [resetPasswords, setResetPasswords] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [newUser, setNewUser] = useState({ name: '', email: '', mobile: '' });
    const [selectedBulkUsers, setSelectedBulkUsers] = useState([]);
    const [allCandidates, setAllCandidates] = useState([]);
    const [bulkSearchTerm, setBulkSearchTerm] = useState('');
    const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [candidateToDelete, setCandidateToDelete] = useState(null);
    const [bulkTab, setBulkTab] = useState('list');
    const [file, setFile] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

    useEffect(() => {
        const fetchCandidates = async () => {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${serverURL}/reports/registered/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCandidates(response.data);
            setFilteredCandidates(response.data);
        };
        fetchCandidates();
    }, [id]);

    useEffect(() => {
        const fetchAllCandidates = async () => {
            setIsLoadingCandidates(true);
            const token = localStorage.getItem('token');
            try {
                const response = await axios.get(
                    `${serverURL}/candidates/search?eventId=${id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setAllCandidates(response.data);
            } catch (error) {
                console.error('Error fetching all candidates:', error);
            } finally {
                setIsLoadingCandidates(false);
            }
        };
        fetchAllCandidates();
    }, [id]);

    useEffect(() => {
        const results = candidates.filter(candidate =>
            candidate.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            candidate.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredCandidates(results);
    }, [searchTerm, candidates]);

    const handleResetPassword = async (candidateId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${serverURL}/users/reset-password`, { userId: candidateId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setResetPasswords(prev => ({ ...prev, [candidateId]: response.data.newPassword }));
        } catch (error) {
            console.error('Error resetting password:', error);
            alert('Failed to reset password. Please try again.');
        }
    };

    const handleDeleteCandidate = (candidateId) => {
        setCandidateToDelete(candidateId);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!candidateToDelete) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${serverURL}/candidates/remove-from-event`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { candidate_id: candidateToDelete, event_id: id }
            });
            toast.success('Candidate deleted successfully');
            const updatedList = candidates.filter(c => c.id !== candidateToDelete);
            setCandidates(updatedList);
            setFilteredCandidates(updatedList);
            const allResp = await axios.get(
                `${serverURL}/candidates/search?eventId=${id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setAllCandidates(allResp.data);
        } catch (error) {
            console.error('Error deleting candidate:', error);
            toast.error('Failed to delete candidate');
        } finally {
            setDeleteModalOpen(false);
            setCandidateToDelete(null);
        }
    };

    const handleAddUser = async () => {
        if (!selectedCandidate?.candidate_id) {
            toast.error('Please select a candidate');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${serverURL}/candidates/register-to-event`,
                { candidate_id: selectedCandidate.candidate_id, event_id: parseInt(id) },
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            toast.success('Candidate added successfully!');
            const updatedResponse = await axios.get(`${serverURL}/reports/registered/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCandidates(updatedResponse.data);
            setFilteredCandidates(updatedResponse.data);
            setIsModalOpen(false);
            setSelectedCandidate(null);
            setNewUser({ name: '', email: '', mobile: '' });
        } catch (error) {
            console.error('Error adding candidate:', error);
            toast.error(error.response?.data?.message || 'Failed to add candidate. Please try again.');
        }
    };

    const handleBulkUpload = async () => {
        if (!file) {
            toast.error('Please select a file');
            return;
        }
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const token = localStorage.getItem('token');
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                if (jsonData.length === 0) { toast.error('File is empty'); return; }
                let successCount = 0;
                let errorCount = 0;
                for (const row of jsonData) {
                    const candidateData = {};
                    Object.keys(row).forEach(key => { candidateData[key.toLowerCase()] = row[key]; });
                    if (!candidateData.email || !candidateData.name) { errorCount++; continue; }
                    try {
                        await axios.post(
                            `${serverURL}/candidates/register-to-event`,
                            {
                                event_id: parseInt(id, 10),
                                name: candidateData.name,
                                email: candidateData.email,
                                mobile: candidateData.mobile || '',
                                id_number: candidateData.id_number || candidateData.id || candidateData['employee id'] || candidateData['employee_id'] || '',
                                institution: candidateData.institution || '',
                            },
                            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
                        );
                        successCount++;
                    } catch (err) {
                        console.error('Error registering candidate from bulk upload:', err);
                        if (err.response?.status === 403) {
                            toast.error(err.response?.data?.message || 'Candidate limit reached.');
                            break;
                        }
                        errorCount++;
                    }
                }
                if (successCount > 0) {
                    toast.success(`${successCount} candidate(s) added successfully`);
                }
                if (errorCount > 0) {
                    toast.error(`${errorCount} row(s) could not be imported`);
                }
                const updatedResponse = await axios.get(`${serverURL}/reports/registered/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setCandidates(updatedResponse.data);
                setFilteredCandidates(updatedResponse.data);
                setIsBulkModalOpen(false);
                setFile(null);
            } catch (error) {
                console.error('Error parsing file:', error);
                toast.error('Error processing file');
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleBulkAdd = async () => {
        if (selectedBulkUsers.length === 0) { toast.error('Please select at least one user'); return; }
        try {
            const token = localStorage.getItem('token');
            let successCount = 0;
            let errorCount = 0;
            for (const candidateId of selectedBulkUsers) {
                try {
                    await axios.post(
                        `${serverURL}/candidates/register-to-event`,
                        { candidate_id: candidateId, event_id: parseInt(id) },
                        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
                    );
                    successCount++;
                } catch (error) {
                    if (error.response?.status === 409) continue;
                    if (error.response?.status === 403) {
                        toast.error(error.response?.data?.message || 'Candidate limit reached.');
                        break;
                    }
                    errorCount++;
                }
            }
            if (successCount > 0) toast.success(`${successCount} candidate(s) added successfully!`);
            if (errorCount > 0) toast(`${errorCount} candidate(s) failed to add`);
            const updatedResponse = await axios.get(`${serverURL}/reports/registered/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCandidates(updatedResponse.data);
            setFilteredCandidates(updatedResponse.data);
            setIsBulkModalOpen(false);
            setSelectedBulkUsers([]);
        } catch (error) {
            console.error('Error in bulk add:', error);
            toast.error('Failed to add candidates');
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-campus-bg">
            <main className="flex-grow p-4 sm:p-6">

                {/* ── Portal: inject action buttons (right) into global header bar ── */}
                {document.getElementById('global-header-actions') && ReactDOM.createPortal(
                    <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-campus-primary hover:bg-campus-primary-dark text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm whitespace-nowrap"
                        >
                            Add User
                        </button>
                        <button
                            onClick={() => setIsBulkModalOpen(true)}
                            className="bg-campus-text-primary hover:bg-campus-text-body text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors shadow-sm whitespace-nowrap"
                        >
                            Bulk Add
                        </button>
                    </div>,
                    document.getElementById('global-header-actions')
                )}

                {/* ── Toolbar bar ────────────────────────────────────── */}
                <div className="flex items-center bg-campus-surface border border-campus-border rounded-xl shadow-sm mb-6 overflow-hidden">

                    {/* Left: Candidate count */}
                    <div className="px-4 py-2.5 border-r border-campus-border whitespace-nowrap flex-shrink-0">
                        <p className="text-campus-text-body font-medium text-sm">
                            Total: <span className="text-campus-text-primary font-bold">{candidates.length}</span>
                            {filteredCandidates.length !== candidates.length && (
                                <span className="ml-1.5 text-xs text-campus-text-body">({filteredCandidates.length} shown)</span>
                            )}
                        </p>
                    </div>

                    {/* Center: Search bar — fills remaining space */}
                    <div className="relative flex-1">
                        <FaSearch className="absolute left-3.5 top-1/3 -translate-y-1/2 text-campus-text-body pointer-events-none" size={13} />
                        <label htmlFor="candidate-search" className="sr-only">Search candidates</label>
                        <input
                            id="candidate-search"
                            type="text"
                            placeholder="Search by name or email…"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 text-sm bg-transparent text-campus-text-primary placeholder-campus-text-body focus:outline-none focus:bg-campus-bg transition-colors"
                        />
                    </div>

                    {/* Right: View mode toggle */}
                    <div className="flex items-center border-l border-campus-border px-2 py-1.5 gap-0.5 flex-shrink-0">
                        <button
                            onClick={() => setViewMode('grid')}
                            aria-label="Grid view"
                            title="Grid view"
                            className={`flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 ${viewMode === 'grid'
                                ? 'bg-campus-primary text-white shadow-sm'
                                : 'text-campus-text-body hover:bg-campus-bg hover:text-campus-text-primary'
                                }`}
                        >
                            <BsGrid3X3Gap size={15} />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            aria-label="List view"
                            title="List view"
                            className={`flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 ${viewMode === 'list'
                                ? 'bg-campus-primary text-white shadow-sm'
                                : 'text-campus-text-body hover:bg-campus-bg hover:text-campus-text-primary'
                                }`}
                        >
                            <BsList size={18} />
                        </button>
                    </div>
                </div>

                {/* ── GRID VIEW ─────────────────────────────────────────── */}
                {viewMode === 'grid' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                        {filteredCandidates.length > 0 ? (
                            filteredCandidates.map(candidate => (
                                <div key={candidate.id} className="bg-campus-surface p-5 rounded-2xl shadow-card border border-campus-border hover:shadow-hover transition-all duration-200 flex flex-col">
                                    <div className="flex-1">
                                        <h3 className="font-heading font-bold text-base text-campus-text-primary mb-1 truncate">{candidate.name}</h3>
                                        <p className="text-campus-text-body text-xs mb-1 truncate">Email: <span className="font-medium">{candidate.email}</span></p>
                                        <p className="text-campus-text-body text-xs mb-3">Status: <span className="font-medium text-campus-text-primary">{candidate.status}</span></p>
                                    </div>
                                    {resetPasswords[candidate.id] && (
                                        <p className="text-campus-success bg-green-50 p-2 rounded-lg text-xs mb-3 font-mono border border-green-200">New Password: {resetPasswords[candidate.id]}</p>
                                    )}
                                    <div className="flex flex-wrap gap-2 pt-3 border-t border-campus-border">
                                        <button onClick={() => handleResetPassword(candidate.id)} className="flex-1 bg-campus-bg border border-campus-border text-campus-text-primary px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-campus-surface transition-colors">
                                            Reset Password
                                        </button>
                                        <button onClick={() => handleDeleteCandidate(candidate.id)} className="flex-1 bg-campus-primary hover:bg-campus-primary-dark text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-campus-text-body bg-campus-surface p-8 rounded-xl border border-campus-border text-center col-span-full">No candidates found.</p>
                        )}
                    </div>
                )}

                {/* ── LIST VIEW ─────────────────────────────────────────── */}
                {viewMode === 'list' && (
                    <div className="bg-campus-surface rounded-xl shadow-card border border-campus-border overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-campus-border">
                                <thead className="bg-campus-bg">
                                    <tr>
                                        {['Name', 'Email', 'Status', 'Actions'].map(col => (
                                            <th key={col} className="px-6 py-3.5 text-left text-xs font-semibold text-campus-text-body uppercase tracking-wider">{col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-campus-surface divide-y divide-campus-border">
                                    {filteredCandidates.length > 0 ? (
                                        filteredCandidates.map(candidate => (
                                            <tr key={candidate.id} className="hover:bg-campus-bg transition-colors duration-150">
                                                <td className="px-6 py-3.5 whitespace-nowrap">
                                                    <div className="text-sm font-semibold text-campus-text-primary">{candidate.name}</div>
                                                    {resetPasswords[candidate.id] && (
                                                        <div className="text-xs text-campus-success font-mono mt-0.5">New pwd: {resetPasswords[candidate.id]}</div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-3.5 whitespace-nowrap text-sm text-campus-text-body">{candidate.email}</td>
                                                <td className="px-6 py-3.5 whitespace-nowrap">
                                                    {(() => {
                                                        let statusClass = 'bg-gray-100 text-campus-text-body';
                                                        if (candidate.status === 'completed') statusClass = 'bg-green-100 text-green-700';
                                                        else if (candidate.status === 'in_progress') statusClass = 'bg-blue-100 text-blue-700';
                                                        return (
                                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusClass}`}>
                                                                {candidate.status}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-6 py-3.5 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleResetPassword(candidate.id)}
                                                            className="px-3 py-1.5 text-xs font-medium text-campus-text-primary bg-campus-bg border border-campus-border rounded-lg hover:bg-campus-surface transition-colors"
                                                        >
                                                            Reset Pwd
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteCandidate(candidate.id)}
                                                            className="px-3 py-1.5 text-xs font-medium text-white bg-campus-primary hover:bg-campus-primary-dark rounded-lg transition-colors"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-12 text-center text-campus-text-body text-sm">No candidates found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </main>

            {/* Add User Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4" style={{ zIndex: 9999 }}>
                    <div className="bg-campus-surface p-6 rounded-2xl shadow-soft w-full max-w-md">
                        <h2 className="text-xl font-heading font-bold text-campus-text-primary mb-4 border-b border-campus-border pb-3">Add User</h2>
                        <label htmlFor="candidate-select" className="block text-sm font-medium text-campus-text-primary mb-2">Select Candidate</label>
                        <CandidateSelect
                            id="candidate-select"
                            eventId={id}
                            value={selectedCandidate}
                            onChange={(candidate) => {
                                setSelectedCandidate(candidate);
                                if (candidate) {
                                    setNewUser({ name: candidate.name, email: candidate.email, mobile: candidate.mobile || '', candidate_id: candidate.candidate_id });
                                } else {
                                    setNewUser({ name: '', email: '', mobile: '' });
                                }
                            }}
                            isClearable={true}
                        />
                        <div className="mt-4 p-4 bg-campus-bg rounded-xl border border-campus-border">
                            <p className="text-sm text-campus-text-body mb-2"><strong className="text-campus-text-primary">Email:</strong> {newUser.email || 'N/A'}</p>
                            <p className="text-sm text-campus-text-body"><strong className="text-campus-text-primary">Mobile:</strong> {newUser.mobile || 'N/A'}</p>
                        </div>
                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-campus-border">
                            <button
                                onClick={() => { setIsModalOpen(false); setSelectedCandidate(null); setNewUser({ name: '', email: '', mobile: '' }); }}
                                className="px-5 py-2 rounded-lg border border-campus-border text-campus-text-body hover:bg-campus-bg font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddUser}
                                disabled={!selectedCandidate}
                                className={`px-6 py-2 rounded-lg text-white font-medium transition-colors ${selectedCandidate ? 'bg-campus-primary hover:bg-campus-primary-dark shadow-sm' : 'bg-gray-300 cursor-not-allowed'}`}
                            >
                                Add User
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Add Modal */}
            {isBulkModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4" style={{ zIndex: 9999 }}>
                    <div className="bg-campus-surface rounded-2xl shadow-soft w-full max-w-3xl flex flex-col overflow-hidden" style={{ height: '700px' }}>
                        <div className="p-6 border-b border-campus-border bg-campus-surface">
                            <h2 className="text-2xl font-heading font-bold text-campus-text-primary">Add Bulk Users</h2>
                        </div>
                        <div className="flex border-b border-campus-border">
                            <button
                                className={`flex-1 py-3 text-sm font-semibold transition-all duration-200 ${bulkTab === 'list' ? 'border-b-2 border-campus-primary text-campus-primary bg-red-50' : 'text-campus-text-body hover:text-campus-text-primary hover:bg-campus-bg'}`}
                                onClick={() => setBulkTab('list')}
                            >Select Users</button>
                            <button
                                className={`flex-1 py-3 text-sm font-semibold transition-all duration-200 ${bulkTab === 'upload' ? 'border-b-2 border-campus-primary text-campus-primary bg-red-50' : 'text-campus-text-body hover:text-campus-text-primary hover:bg-campus-bg'}`}
                                onClick={() => setBulkTab('upload')}
                            >Upload File</button>
                        </div>

                        {bulkTab === 'list' && (
                            <div className="flex flex-col flex-1 overflow-hidden">
                                <div className="p-4 border-b border-campus-border bg-campus-surface flex gap-3 items-center">
                                    <div className="relative flex-1">
                                        <label htmlFor="bulk-search-input" className="sr-only">Search candidates by name or email</label>
                                        <input
                                            id="bulk-search-input"
                                            type="text"
                                            placeholder="Search by name or email..."
                                            value={bulkSearchTerm}
                                            onChange={(e) => setBulkSearchTerm(e.target.value)}
                                            className="w-full pl-4 pr-4 py-2 border border-campus-border rounded-lg focus:ring-2 focus:ring-campus-primary focus:border-campus-primary transition-all outline-none bg-campus-surface text-campus-text-primary placeholder-campus-text-body"
                                        />
                                    </div>
                                    <button
                                        onClick={() => setBulkSearchTerm('@vthink.co.in')}
                                        className="px-4 py-2 bg-campus-bg text-campus-text-body rounded-lg border border-campus-border hover:bg-campus-surface hover:text-campus-text-primary transition-all text-sm font-medium whitespace-nowrap"
                                    >vThink Filter</button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-2 bg-campus-bg">
                                    <div className="space-y-2 px-2 pb-2">
                                        {allCandidates
                                            .filter(candidate =>
                                                candidate.name.toLowerCase().includes(bulkSearchTerm.toLowerCase()) ||
                                                candidate.email.toLowerCase().includes(bulkSearchTerm.toLowerCase())
                                            )
                                            .map(candidate => (
                                                <label
                                                    key={candidate.id}
                                                    aria-label={candidate.name}
                                                    className={`flex items-center p-3 rounded-lg border transition-all cursor-pointer ${selectedBulkUsers.includes(candidate.id) ? 'bg-red-50 border-red-200 shadow-sm' : 'bg-campus-surface border-campus-border hover:border-campus-primary/40 hover:shadow-sm'}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedBulkUsers.includes(candidate.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedBulkUsers([...selectedBulkUsers, candidate.id]);
                                                            } else {
                                                                setSelectedBulkUsers(selectedBulkUsers.filter(uid => uid !== candidate.id));
                                                            }
                                                        }}
                                                        className="w-4 h-4 text-campus-primary border-campus-border rounded focus:ring-campus-primary mr-4 mt-0.5"
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className={`font-semibold truncate ${selectedBulkUsers.includes(candidate.id) ? 'text-campus-primary' : 'text-campus-text-primary'}`}>{candidate.name}</div>
                                                        <div className="text-sm text-campus-text-body truncate">{candidate.email}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        {!isLoadingCandidates && allCandidates.length === 0 && (
                                            <div className="text-center py-12 text-campus-text-body">No candidates found</div>
                                        )}
                                    </div>
                                </div>
                                <div className="p-4 border-t border-campus-border bg-campus-bg flex justify-between items-center">
                                    <div className="text-sm text-campus-text-body font-medium">{selectedBulkUsers.length} selected</div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => { setIsBulkModalOpen(false); setSelectedBulkUsers([]); setBulkSearchTerm(''); }}
                                            className="px-5 py-2 rounded-lg text-campus-text-body bg-campus-surface border border-campus-border hover:bg-campus-bg font-medium transition-colors"
                                        >Cancel</button>
                                        <button
                                            onClick={handleBulkAdd}
                                            disabled={selectedBulkUsers.length === 0}
                                            className={`px-5 py-2 rounded-lg text-white font-medium shadow-sm transition-all ${selectedBulkUsers.length > 0 ? 'bg-campus-primary hover:bg-campus-primary-dark' : 'bg-gray-300 cursor-not-allowed'}`}
                                        >Add Selected Users</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {bulkTab === 'upload' && (
                            <div className="flex flex-col flex-1 p-8 items-center justify-center bg-campus-bg">
                                <div className="w-full max-w-md bg-campus-surface p-8 rounded-2xl shadow-sm border border-campus-border text-center hover:shadow-hover transition-all">
                                    <h3 className="text-lg font-bold text-campus-text-primary mb-2">Upload Candidate File</h3>
                                    <p className="text-sm text-campus-text-body mb-6">Support for .xls and .xlsx files. Ensure Name and Email columns exist.</p>
                                    <div className="relative mb-6">
                                        <label htmlFor="bulk-file-upload" className="sr-only">Upload Excel file (.xls, .xlsx)</label>
                                        <input
                                            id="bulk-file-upload"
                                            type="file"
                                            accept=".xls,.xlsx"
                                            onChange={(e) => setFile(e.target.files[0])}
                                            className="block w-full text-sm text-campus-text-body file:mr-4 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-red-50 file:text-campus-primary hover:file:bg-red-100 cursor-pointer border border-dashed border-campus-border rounded-lg p-3 bg-campus-bg"
                                        />
                                    </div>
                                    <div className="flex gap-3 justify-center">
                                        <button onClick={() => setIsBulkModalOpen(false)} className="px-5 py-2.5 rounded-lg border border-campus-border text-campus-text-body hover:bg-campus-bg font-medium transition-colors">Cancel</button>
                                        <button onClick={handleBulkUpload} className="px-6 py-2.5 rounded-lg bg-campus-primary text-white font-medium hover:bg-campus-primary-dark transition-all shadow-sm">Process File</button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4" style={{ zIndex: 9999 }}>
                    <div className="bg-campus-surface p-6 rounded-2xl shadow-soft w-full max-w-md border border-campus-border">
                        <h2 className="text-xl font-heading font-bold text-campus-text-primary mb-4 border-b border-campus-border pb-3">Confirm Deletion</h2>
                        <p className="text-campus-text-body mb-6">Are you sure you want to delete.</p>
                        <div className="flex justify-end gap-3 pt-4 border-t border-campus-border">
                            <button onClick={() => setDeleteModalOpen(false)} className="px-5 py-2 rounded-lg border border-campus-border text-campus-text-body hover:bg-campus-bg font-medium transition-colors">Cancel</button>
                            <button onClick={confirmDelete} className="bg-campus-error text-white px-5 py-2 rounded-lg hover:bg-red-600 font-medium transition-colors shadow-sm">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CandidatesList;
